"use client";

import { Suspense, useEffect, useRef } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import axios from "axios";
import { useAuthStore } from "@/store/auth.store";

const BASE_URL = process.env["NEXT_PUBLIC_API_URL"] ?? "http://localhost:4000";

function URLCleanup() {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();

  useEffect(() => {
    if (searchParams.has("rt")) {
      const params = new URLSearchParams(searchParams.toString());
      params.delete("rt");
      const newQuery = params.toString();
      replace(`${pathname}${newQuery ? `?${newQuery}` : ""}`);
    }
  }, [searchParams, pathname, replace]);

  return null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { replace } = useRouter();
  const pathname = usePathname();
  const { user, logout } = useAuthStore();

  // Keep a ref so the one-time effect can read the current pathname
  // without adding it to the dependency array (which caused re-fires on every navigation)
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  // ⚠️  Intentionally runs only ONCE on initial mount.
  //  Subsequent 401s are handled by the axios interceptor in api.ts.
  //  Having `pathname` in deps was the bug: every client-side navigation
  //  re-triggered this, saw user && !accessToken (accessToken is memory-only),
  //  called /auth/refresh, and logged the user out if it failed.
  useEffect(() => {
    if (!user) return;

    let timeoutId: NodeJS.Timeout;

    const resetTimer = () => {
      clearTimeout(timeoutId);
      // 20 minutes = 1200000 ms
      timeoutId = setTimeout(async () => {
        try {
          const rt = useAuthStore.getState().refreshToken;
          await axios.post(
            `${BASE_URL}/api/v1/auth/logout`,
            { refreshToken: rt ?? "" },
            { headers: { "x-refresh-token": rt ?? "" } }
          );
        } catch {
          // ignore
        }
        logout();
        replace("/login");
      }, 1200000);
    };

    const events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart"];
    events.forEach((name) => document.addEventListener(name, resetTimer, true));

    resetTimer(); // init

    return () => {
      clearTimeout(timeoutId);
      events.forEach((name) => document.removeEventListener(name, resetTimer, true));
    };
  }, [user, logout, replace]);

  return (
    <>
      <Suspense fallback={null}>
        <URLCleanup />
      </Suspense>
      {children}
    </>
  );
}
